import type { GuidanceLevel, Locale } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom'
import { Logo } from './components/Logo.js'
import { Button, Select } from './components/ui.js'
import { api, ApiError } from './lib/api.js'
import { GuidanceProvider } from './lib/guidance.js'
import { useLocale, useT } from './lib/i18n.js'
import { AccountPage } from './pages/AccountPage.js'
import { IntakeInboxPage } from './pages/IntakeInboxPage.js'
import { IntakePage } from './pages/IntakePage.js'
import { LoginPage } from './pages/LoginPage.js'
import { SharePage } from './pages/SharePage.js'
import { TripPage } from './pages/TripPage.js'
import { TripsPage } from './pages/TripsPage.js'

export function App() {
  return (
    <Routes>
      {/*
        The traveller's route. Outside the advisor shell entirely — no nav, no
        session check, nothing that assumes an account exists. `/s/:token` is a
        permanent contract: a bookmarked link has to keep working across a
        domain move and even a migration off this codebase.
      */}
      <Route path="/s/:token" element={<SharePage />} />

      <Route path="/intake/:agencyId" element={<IntakePage />} />
      <Route path="/login" element={<LoginPage />} />

      <Route element={<AdvisorShell />}>
        <Route path="/trips" element={<TripsPage />} />
        <Route path="/trips/:tripId" element={<TripPage />} />
        <Route path="/intake" element={<IntakeInboxPage />} />
        <Route path="/account" element={<AccountPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/trips" replace />} />
    </Routes>
  )
}

function AdvisorShell() {
  const t = useT()
  const { locale, setLocale } = useLocale()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [settingsOpen, setSettingsOpen] = useState(false)

  const session = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: (_count, error) => !(error instanceof ApiError && error.code === 'unauthorized'),
  })

  const logout = useMutation({
    mutationFn: api.logout,
    async onSuccess() {
      queryClient.clear()
      await navigate('/login')
    },
  })

  if (session.isPending) return <Centered>{t('common.loading')}</Centered>
  if (session.isError) return <Navigate to="/login" replace />

  return (
    <GuidanceProvider level={session.data.user.guidanceLevel}>
      <div className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-5 p-4 sm:gap-6 sm:p-6">
        {/*
          One line on a phone, one row on a laptop.

          Everything on the right — the two selects, her name, sign out — is
          chrome she touches once a month, and on a 390px screen it wrapped to
          fill the first view before the trip appeared at all. Behind a button it
          costs one tap on the rare day she wants it and nothing on every other
          day.
        */}
        <nav className="no-print flex flex-col gap-3 border-b border-slate-200 pb-4">
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/trips"
              className="-mx-2 flex min-h-11 items-center gap-2 px-2 font-display font-semibold sm:mx-0 sm:min-h-0 sm:px-0"
            >
              <Logo />
              {session.data.user.agencyName}
            </Link>

            <Button
              variant="ghost"
              aria-expanded={settingsOpen}
              aria-controls="advisor-settings"
              onClick={() => setSettingsOpen((v) => !v)}
              className="sm:hidden"
            >
              {t('nav.settings')}
            </Button>

            <div className="hidden items-center gap-3 text-sm sm:flex">
              <GuidanceSelect level={session.data.user.guidanceLevel} />
              <LocaleSelect locale={locale} setLocale={setLocale} />
              {/*
                Her name is the way into her account, which is where every
                product puts it. A separate "Account" item would be a fourth
                thing in a row that already wrapped on a phone once.
              */}
              <Link to="/intake" className="text-slate-500 underline-offset-4 hover:underline">
                {t('nav.intake')}
              </Link>
              <Link to="/account" className="text-slate-500 underline-offset-4 hover:underline">
                {session.data.user.displayName}
              </Link>
              <Button variant="quiet" onClick={() => logout.mutate()}>
                {t('nav.signOut')}
              </Button>
            </div>
          </div>

          {settingsOpen && (
            <div id="advisor-settings" className="flex flex-col gap-3 sm:hidden">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('guidance.label')}</span>
                <GuidanceSelect level={session.data.user.guidanceLevel} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('nav.language')}</span>
                <LocaleSelect locale={locale} setLocale={setLocale} />
              </label>
              <Link
                to="/intake"
                onClick={() => setSettingsOpen(false)}
                className="-mx-2 flex min-h-11 items-center px-2 text-sm font-medium text-slate-700"
              >
                {t('nav.intake')}
              </Link>
              <Link
                to="/account"
                onClick={() => setSettingsOpen(false)}
                className="-mx-2 flex min-h-11 items-center px-2 text-sm font-medium text-slate-700"
              >
                {t('nav.account')}
              </Link>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-500">{session.data.user.displayName}</span>
                <Button variant="quiet" onClick={() => logout.mutate()}>
                  {t('nav.signOut')}
                </Button>
              </div>
            </div>
          )}
        </nav>
        <Outlet />
      </div>
    </GuidanceProvider>
  )
}

function LocaleSelect({
  locale,
  setLocale,
}: {
  locale: Locale
  setLocale(next: Locale): void
}) {
  return (
    <Select
      aria-label="Language"
      value={locale}
      onChange={(e) => setLocale(e.target.value as Locale)}
      className="sm:h-8 sm:w-auto sm:text-xs"
    >
      <option value="pt-BR">Português</option>
      <option value="en">English</option>
    </Select>
  )
}

/**
 * Turns the field explanations down.
 *
 * In the nav rather than buried in a settings page, because the person who
 * wants it is the person who has just read the same sentence for the twentieth
 * time. Making them go looking is how a setting like this never gets used and
 * the explanations stay on forever.
 */
function GuidanceSelect({ level }: { level: GuidanceLevel }) {
  const t = useT()
  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: api.setGuidanceLevel,
    // Refetched rather than written into the cache: the server's answer is the
    // one that survives a reload, so it is the one worth rendering.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })

  return (
    <Select
      aria-label={t('guidance.label')}
      value={level}
      disabled={save.isPending}
      onChange={(event) => save.mutate(event.target.value as GuidanceLevel)}
      className="sm:h-8 sm:w-auto sm:text-xs"
    >
      <option value="full">{t('guidance.full')}</option>
      <option value="brief">{t('guidance.brief')}</option>
      <option value="off">{t('guidance.off')}</option>
    </Select>
  )
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center text-sm text-slate-500">
      {children}
    </div>
  )
}
